import React from "react";
import { List, Typography, Space, Button, Input } from "antd";
import RootRow from "@/components/RootRow.jsx"; // твой текущий компонент для дерева
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;

/** Инлайн-компонент черновика (как было у тебя) */
function DraftRow({ placeholder, value, setValue, onSave, onCancel }) {
  return (
    <Space.Compact style={{ width: "100%", marginTop: 6 }}>
      <Input
        autoFocus
        size="middle"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={onSave}
      />
      <Button type="primary" size="middle" icon={<CheckOutlined />} onClick={onSave} />
      <Button size="middle" icon={<CloseOutlined />} onClick={onCancel} />
    </Space.Compact>
  );
}

/**
 * Универсальный MobileCategories:
 * - mode="tree" (по умолчанию) — поведение 1в1 как раньше для категорий
 * - mode="flat" — плоский список (например, единицы измерения). Нужен RowComponent для рендера строки.
 *
 * Параметры:
 * - loading, dataSource
 * - rootName, setRootName, saveRoot, setShowRootDraft
 * - mode: "tree" | "flat" (default "tree")
 * - RowComponent: компонент строки для mode="flat"
 * - draftConfig: { title, placeholder }
 * - forceShowRootDraft: boolean — показать блок черновика независимо от __isRootDraft
 *
 * Для mode="tree" дополнительно:
 * - childDrafts, setChildDrafts, saveChild, onRename, onAddChild, onDelete
 * - expandedRowKeys, setExpandedRowKeys
 *
 * Для mode="flat":
 * - onRename, onDelete — будут прокинуты в RowComponent
 */
export default function MobileCategories({
  loading,
  dataSource = [],

  rootName,
  setRootName,
  saveRoot,
  setShowRootDraft,

  mode = "tree",
  RowComponent,

  draftConfig = { title: "Нова група", placeholder: "Назва групи" },
  forceShowRootDraft = false,

  // tree-режим
  childDrafts,
  setChildDrafts = () => {},
  saveChild,
  onRename,
  onAddChild,
  onDelete,
  expandedRowKeys = [],
  setExpandedRowKeys = () => {},
}) {
  if (loading) return null;

  const isTree = mode === "tree";
  const EffectiveRow = isTree ? RootRow : RowComponent;

  const hasRootDraft =
    forceShowRootDraft ||
    (dataSource.length && dataSource[0]?.__isRootDraft);

  const isExpanded = (id) => expandedRowKeys?.includes(id);
  const toggleExpand = (id) =>
    setExpandedRowKeys((keys) => {
      const set = new Set(keys || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });

  const items = dataSource.filter((x) => !x.__isRootDraft);

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {hasRootDraft && (
        <div style={{ border: "1px solid #e8e8e8", borderRadius: 8, padding: 10 }}>
          <Text strong style={{ fontSize: 14 }}>
            {draftConfig.title || (isTree ? "Нова група" : "Новий елемент")}
          </Text>
          <DraftRow
            placeholder={draftConfig.placeholder || (isTree ? "Назва групи" : "Назва")}
            value={rootName}
            setValue={setRootName}
            onSave={saveRoot}
            onCancel={() => {
              setRootName("");
              setShowRootDraft?.(false);
            }}
          />
        </div>
      )}

      <List
        dataSource={items}
        split={false}
        renderItem={(item) => {
          if (isTree) {
            const expanded = isExpanded(item._id);
            const draftVal = childDrafts?.[item._id];

            return (
              <RootRow
                key={item._id}
                root={item}
                expanded={expanded}
                toggleExpand={toggleExpand}
                childDraftValue={draftVal}
                setChildDraftValue={(v) =>
                  setChildDrafts((m) =>
                    v === undefined
                      ? (() => {
                          const { [item._id]: _, ...rest } = m || {};
                          return rest;
                        })()
                      : { ...(m || {}), [item._id]: v }
                  )
                }
                saveChild={saveChild}
                onRename={onRename}
                onAddChild={onAddChild}
                onDelete={onDelete}
              />
            );
          }

          if (!EffectiveRow) {
            console.warn("For mode='flat' you must pass RowComponent");
            return null;
          }
          return (
            <EffectiveRow
              key={item._id}
              item={item}
              onRename={onRename}
              onDelete={onDelete}
            />
          );
        }}
      />
    </div>
  );
}
